import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth, storage } from '../firebaseConfig';
import { doc, getDoc, collection, getDocs, query, orderBy, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL, deleteObject, uploadBytes } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';

interface Document {
    id: string;
    name: string;
    documentName?: string;
    uploadTime: Date;
    status: 'requested' | 'uploaded';
    downloadUrl?: string;
}

interface UploadedDocumentsProps {
    write: boolean;
}

const UploadedDocuments = ({ write }: UploadedDocumentsProps) => {

     const { currentSubAgent } = useSubAgentAuth();
     
    const navigate = useNavigate();
    const { leadId } = useParams();
    const [companyId, setCompanyId] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [documents, setDocuments] = useState<Document[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [loadingDocId, setLoadingDocId] = useState<string | null>(null);

    useEffect(() => {
        fetchData();
    }, [leadId]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const currentUser = auth.currentUser;
            if (!currentUser || !leadId) return;

            let companyId: string | undefined;
            // Get user document to get companyId
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
            }
            if (!companyId) return;

            setCompanyId(companyId);

            // Fetch documents
            const documentsRef = collection(db, `companies/${companyId}/leads/${leadId}/documents`);
            const documentsQuery = query(documentsRef, orderBy('uploadTime', 'desc'));
            const documentsSnapshot = await getDocs(documentsQuery);

            const documentsData: Document[] = [];
            for (const doc of documentsSnapshot.docs) {
                const data = doc.data();
                if (data.documentName) {
                    // This is an uploaded document, get the download URL
                    const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${data.documentName}`);
                    const downloadUrl = await getDownloadURL(storageRef);
                    documentsData.push({
                        id: doc.id,
                        name: data.name,
                        documentName: data.documentName,
                        uploadTime: data.uploadTime.toDate(),
                        status: 'uploaded',
                        downloadUrl
                    });
                } else {
                    documentsData.push({
                        id: doc.id,
                        name: data.name,
                        uploadTime: data.uploadTime.toDate(),
                        status: 'requested'
                    });
                }
            }
            setDocuments(documentsData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDocumentClick = async (document: Document) => {
        if (document.downloadUrl) {
            window.open(document.downloadUrl, '_blank');
        }
    };

    const handleDelete = async (document: Document) => {
        try {
            setLoadingDocId(document.id);
            if (!companyId || !leadId) return;

            // If document was uploaded, delete from storage first
            if (document.documentName) {
                const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${document.documentName}`);
                await deleteObject(storageRef);
            }

            // Delete Firestore document
            const docRef = doc(db, `companies/${companyId}/leads/${leadId}/documents/${document.id}`);
            await deleteDoc(docRef);

            await fetchData(); // Refresh the documents list
        } catch (error) {
            console.error("Error deleting document:", error);
        } finally {
            setLoadingDocId(null);
        }
    };

    const handleUploadExisting = async (document: Document) => {
        if (!fileInputRef.current) return;
        fileInputRef.current.click();
        fileInputRef.current.onchange = async (event: Event) => {
            const target = event.target as HTMLInputElement;
            try {
                setLoadingDocId(document.id);
                const file = target.files?.[0];
                if (!file || !companyId || !leadId) return;

                // Check file size (100MB limit)
                if (file.size > 100 * 1024 * 1024) {
                    alert("File size should not exceed 100MB");
                    return;
                }

                // Check file type
                if (!['application/pdf', 'image/jpeg', 'image/jpg'].includes(file.type)) {
                    alert("Only PDF, JPEG, and JPG files are allowed");
                    return;
                }

                const fileName = `${uuidv4()}_${file.name}`;
                const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${fileName}`);

                // Upload file to storage
                await uploadBytes(storageRef, file);
                const downloadUrl = await getDownloadURL(storageRef);

                // Update existing document
                const docRef = doc(db, `companies/${companyId}/leads/${leadId}/documents/${document.id}`);
                await updateDoc(docRef, {
                    documentName: fileName,
                });

                await fetchData(); // Refresh the documents list
            } catch (error) {
                console.error("Error uploading document:", error);
            } finally {
                setLoadingDocId(null);
                if (target) target.value = ''; // Reset file input
            }
        };
    };

    const formatUploadTime = (date: Date) => {
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        const timeString = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
        const dateString = date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short'
        });

        return `${dateString}, ${timeString} · ${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    };

    return (
        <div>
            <div className="mt-8 ml-12 w-3/4">
                <h2 className="font-montserrat font-medium text-2xl text-gray-900 mb-3">Submitted Documents</h2>

                <div className="space-y-4">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg"
                        className={`hidden ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        disabled={!write}
                    />

                    {documents.map(doc => (
                        <div key={doc.id} className="relative">
                            <label className="block font-montserrat font-normal text-base text-black mb-2">
                                {doc.name}
                            </label>
                            <div
                                onClick={() => handleDocumentClick(doc)}
                                className={`relative w-full p-4 bg-white border border-gray-200 rounded-lg ${doc.downloadUrl ? 'cursor-pointer hover:bg-gray-50' : ''
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/${doc.documentName ? (
                                                    doc.documentName.toLowerCase().endsWith('.pdf')
                                                        ? 'catppuccin.svg'
                                                        : doc.documentName.toLowerCase().match(/\.(jpg|jpeg)$/)
                                                            ? 'teenyicons.svg'
                                                            : 'catppuccin.svg'
                                                ) : 'catppuccin.svg'
                                                }`}
                                            alt="Document Icon"
                                            className="w-6 h-6 mr-3"
                                        />
                                        <div>
                                            <p className="text-sm text-gray-600">
                                                {formatUploadTime(doc.uploadTime)}
                                            </p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                {doc.status === 'requested' ? 'Pending Upload' : (
                                                    doc.documentName ? doc.documentName.split('_').slice(1).join('_') : 'Uploaded'
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {doc.status === 'requested' && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleUploadExisting(doc);
                                                }}
                                                className="px-3 py-1 text-sm text-white rounded-full bg-gradient-to-r from-[#3483BE] to-[#02385E] hover:opacity-90 min-w-[70px]"
                                                disabled={loadingDocId === doc.id}
                                            >
                                                {loadingDocId === doc.id ? (
                                                    <div className="flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    </div>
                                                ) : 'Upload'}
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(doc);
                                            }}
                                            className="px-3 py-1 text-sm text-white rounded-full bg-gradient-to-r from-[#FFA782] to-[#FC583D] hover:opacity-90 min-w-[70px]"
                                            disabled={loadingDocId === doc.id}
                                        >
                                            {loadingDocId === doc.id ? (
                                                <div className="flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                </div>
                                            ) : 'Delete'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default UploadedDocuments;